/**
 * Adapter Tela 7 Centro de Alertas — `ConflictAlertRaw` (contrato engine) → view-models UI.
 *
 * R15 triangulado contra:
 *   - features/supervisor/types.ts (ConflictAlertRaw / McpBlockEventRaw)
 *   - features/supervisor/view-models.ts (ConflictAlertVM e satélites)
 *   - apps/engine/src/modules/adversarial/conflict-alerts/ (controllers + prisma)
 *
 * Função pura. Placeholders honestos (`'—'`) onde o backend não fornece (padrão Item 317):
 *   - parties: backend só dá IDs (sem nome/role/kind) — `nameFor` resolve presence, role = '—' (Item 324).
 *   - trigger: sem campo no backend (gap).
 *   - chain.total/history: real só via /chain (drawer); aqui stub (position=total, history vazio).
 *   - impact.blockedMcpToolCalls/auditLogId: preenchidos no drawer via /mcp-block-events.
 */
import { relativeTime } from '../../../conversations/format';

import type { ConflictAlertRaw, McpBlockEventRaw } from '../../types';
import type {
  ConflictAlertVM,
  ConflictAlertChainStepVM,
  ConflictAlertBlockedMcpCallVM,
  ConflictAlertHistoryRowVM,
  ConflictAlertStatusVM,
} from '../../view-models';

const PLACEHOLDER = '—';

/** Humanização PT-BR das 5 categorias canônicas (Item 320; o design tem 7 alertTypes). */
export const CATEGORY_LABELS: Record<string, string> = {
  category_a_societario: 'Societário',
  category_b_papel_duplo: 'Papel duplo',
  category_c_informacao_privilegiada: 'Informação privilegiada',
  category_d_exclusividade: 'Exclusividade',
  category_e_capacidade_limitada: 'Capacidade limitada',
};

/** Humanização PT-BR dos 7 status canônicos + legados da Fatia C (tab Histórico — T7-4). */
export const STATUS_LABELS: Record<string, string> = {
  detected: 'Detectado',
  mitigated: 'Mitigado',
  accepted_with_justification: 'Aceito c/ just.',
  false_positive: 'Falso positivo',
  resolved: 'Resolvido',
  revoked: 'Revogado',
  auto_resolved: 'Auto-resolvido',
  // Legados (Fatia C) que a query de histórico ainda retorna (review T7).
  open: 'Aberto',
  acknowledged: 'Reconhecido',
  accepted: 'Aceito',
};

/** Token desconhecido → legível ('some_legacy_state' → 'Some legacy state'), nunca cru. */
function humanizeStatus(token: string): string {
  const s = token.replace(/_/g, ' ').trim();
  return s.length === 0 ? token : s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Normaliza o status do backend. Os 7 canônicos passam direto; valores legados
 * (open/acknowledged/accepted da Fatia C) degradam graciosos (mantém cru — o
 * StatusBadge esconde o que não reconhece). Hoje é identidade; centraliza o ponto
 * de mapeamento caso o backend exponha aliases no futuro.
 */
export function normalizeStatus(status: string): string {
  return status;
}

/** HH:MM local a partir de um ISO. */
function fmtClock(iso: string): string {
  const d = new Date(iso);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Tempo relativo sem o prefixo "há " (a UI prepende "há " — card + drawer). */
function fmtAgo(iso: string, now: Date): string {
  return relativeTime(iso, now).replace(/^há /, '');
}

/** Data local YYYY-MM-DD a partir de um ISO (tab Histórico — auditoria por dia). */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear().toString().padStart(4, '0');
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Duração humana simples entre dois ISO ("45min" / "2h" / "3d"). */
function durationLabel(fromIso: string, toIso: string): string {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min}min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/** Dias inteiros (arredondados) de `fromIso` até `toIso`. */
function diffDays(fromIso: string, toIso: string): number {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  return Math.round(ms / 86_400_000);
}

export function adaptConflictAlertToVM(
  raw: ConflictAlertRaw,
  nameFor: (id: string) => string,
  now: Date = new Date(),
): ConflictAlertVM {
  const alertType = CATEGORY_LABELS[raw.category] ?? raw.category;
  const conversationId = raw.conversationId ?? PLACEHOLDER;
  const isPaused =
    raw.conversationId != null && raw.pausedConversationIds.includes(raw.conversationId);

  return {
    id: raw.id,
    severity: raw.severity,
    alertType,
    status: normalizeStatus(raw.status),
    title: raw.detectionContext ?? `Conflito de interesse · ${alertType}`,
    description: raw.rationale ?? raw.detectionContext ?? PLACEHOLDER,
    detectedAt: fmtClock(raw.createdAt),
    detectedAgo: fmtAgo(raw.createdAt, now),
    // GAP Item 324: backend só tem IDs. nameFor resolve users via presence; role '—' honesto.
    parties: raw.participantsInvolved.map((id) => ({
      kind: 'person' as const,
      id,
      // Item 324 (defer): backend só tem IDs; sem resolução → rótulo honesto, nunca id cru.
      name: nameFor(id) || 'Pessoa sem identificação',
      role: PLACEHOLDER,
      meta: '',
    })),
    conversation: { id: conversationId, status: isPaused ? 'paused' : 'active' },
    agent: raw.detectionAgent ?? PLACEHOLDER,
    trigger: PLACEHOLDER, // GAP: sem campo trigger no backend.
    confidence: raw.detectionConfidence != null ? Number(raw.detectionConfidence) : null,
    // total real só via /chain (drawer); history preenchido no drawer.
    // chainOriginId: o drawer busca a chain pela origem, não pelo id próprio (review T7).
    chain: {
      position: raw.chainPosition,
      total: raw.chainPosition,
      history: [],
      chainOriginId: raw.chainOriginId,
    },
    // blockedMcpToolCalls/auditLogId preenchidos no drawer via /mcp-block-events.
    impact: {
      pausedConversationIds: raw.pausedConversationIds,
      blockedMcpToolCalls: [],
      auditLogId: null,
    },
    operationId: null, // não vem na lista; só no detalhe (drawer).
    dualApprovalRequired: raw.chainPosition >= 3,
    version: raw.version,
    bondsInvolved: raw.bondsInvolved,
    reopened: raw.reopenedAt != null,
    // T7-4: só o GET /:id seta esse flag (lista não redacta). Spread condicional
    // p/ não atribuir `undefined` ao prop opcional (exactOptionalPropertyTypes).
    ...(raw.partiesRedacted !== undefined ? { partiesRedacted: raw.partiesRedacted } : {}),
  };
}

export function adaptConflictAlertsToVM(
  rawList: ConflictAlertRaw[],
  nameFor: (id: string) => string,
  now: Date = new Date(),
): ConflictAlertVM[] {
  return rawList.map((raw) => adaptConflictAlertToVM(raw, nameFor, now));
}

/**
 * Mapeia um ConflictAlertRaw resolvido/histórico → linha do tab Histórico (T7-4).
 * `detectedAt`/resolução por DIA (auditoria de 90 dias, não relógio). `byUser`
 * resolve resolvedByUserId, senão mitigatedByUserId. `at` cai resolvedAt →
 * mitigatedAt → createdAt. `ttlDays` só quando há acceptanceExpiresAt.
 */
export function adaptHistoryRow(
  raw: ConflictAlertRaw,
  nameFor: (id: string) => string,
  now: Date = new Date(),
): ConflictAlertHistoryRowVM {
  const alertType = CATEGORY_LABELS[raw.category] ?? raw.category;
  const norm = normalizeStatus(raw.status);
  const resolvedBy =
    raw.resolvedByUserId != null
      ? nameFor(raw.resolvedByUserId)
      : raw.mitigatedByUserId != null
        ? nameFor(raw.mitigatedByUserId)
        : undefined;
  // exactOptionalPropertyTypes: só inclui byUser/ttlDays quando presentes.
  const resolution: ConflictAlertHistoryRowVM['resolution'] = {
    status: norm,
    label: STATUS_LABELS[norm] ?? humanizeStatus(norm),
    at: fmtDate(raw.resolvedAt ?? raw.mitigatedAt ?? raw.createdAt),
    ...(resolvedBy != null ? { byUser: resolvedBy } : {}),
    ...(raw.acceptanceExpiresAt != null
      ? { ttlDays: diffDays(now.toISOString(), raw.acceptanceExpiresAt) }
      : {}),
  };
  const parties = raw.participantsInvolved.map(nameFor).join(' ↔ ');
  return {
    id: raw.id,
    severity: raw.severity,
    alertType,
    detectedAt: fmtDate(raw.createdAt),
    title: raw.detectionContext ?? `Conflito de interesse · ${alertType}`,
    partiesShort: parties !== '' ? parties : PLACEHOLDER,
    mitigationTime:
      raw.mitigatedAt != null ? durationLabel(raw.createdAt, raw.mitigatedAt) : PLACEHOLDER,
    resolution,
  };
}

export function adaptHistoryRows(
  rawList: ConflictAlertRaw[],
  nameFor: (id: string) => string,
  now: Date = new Date(),
): ConflictAlertHistoryRowVM[] {
  return rawList.map((raw) => adaptHistoryRow(raw, nameFor, now));
}

/**
 * Mapeia a chain de reincidência (`GET /chain` → `ConflictAlertRaw[]`) em steps
 * do timeline. Cada raw da chain é um ConflictAlert próprio — usa-se a
 * severity/status REAL de cada um (não proxy do alerta atual). `id` do step
 * casa com o id do alerta da chain; o drawer destaca o step cujo id == alert.id.
 * `note` honesto: detectionContext do step, senão placeholder.
 */
export function adaptChainToSteps(chain: ConflictAlertRaw[]): ConflictAlertChainStepVM[] {
  return [...chain]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((c) => ({
      id: c.id,
      at: fmtClock(c.createdAt),
      severity: c.severity,
      status: normalizeStatus(c.status) as ConflictAlertStatusVM,
      note: c.detectionContext ?? PLACEHOLDER,
    }));
}

/** Mapeia MCPBlockEvent[] (/mcp-block-events do drawer) → blockedMcpToolCalls do VM. */
export function adaptMcpBlockEvents(events: McpBlockEventRaw[]): ConflictAlertBlockedMcpCallVM[] {
  return events.map((e) => ({
    at: fmtClock(e.attemptedAt),
    tool: e.toolName,
    status: e.reason,
  }));
}
